import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DonationCardService } from '../../../services/donation-card.service';
import { DonationFlagsService } from '../../../services/donation-flags.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import {
  AddDonationFlagAndAssignToDonationViewModel
} from '../../../models/DonationCardModels';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { GenericButtonComponent } from '../../../../global/component/generic/generic-button/generic-button.component';

@Component({
  selector: 'app-donation-flags-tab',
  standalone: true,
  imports: [
    GenericButtonComponent,
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    DialogModule, 
    AutoCompleteModule, 
    InputSwitchModule, 
    FormsModule, 
    ReactiveFormsModule,
    ToastModule 
  ],
  templateUrl: './donation-flags-tab.component.html',
  styleUrl: './donation-flags-tab.component.scss',
  providers: [DonationFlagsService, DonationCardService, MessageService]
})
export class DonationFlagsTabComponent implements OnInit {
  @Input() donationId!: number;
  @Input() campaignId!: number;
  @Input() isReadOnlyMode: boolean = false;
  flags: any[] = [];
  filteredFlags: any[] = [];
  showAddEditDialog: boolean = false;
  showDeleteDialog: boolean = false;
  isEditMode: boolean = false;
  isAddMode: boolean = false; 
  donationFlagFormGroup: FormGroup;
  flagToDelete: any;

  constructor(
    private service: DonationCardService,
    private donationFlagsService: DonationFlagsService,
    private formBuilder: FormBuilder,
    private messageService: MessageService
  ) {
    this.donationFlagFormGroup = this.formBuilder.group({
      id: [0],
      selectedFlag: [null],
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      isAddMode: [false] // Add isAddMode control for binding
    });
  }

  loadFlags(): void {
    this.service.getFlags(this.donationId).subscribe({
      next: (data) => {
        this.flags = data;
      },
      error: (err) => {
        console.error('Error loading flags:', err);
      }
    });
  }

  toggleMode(): void {
    this.isAddMode = this.donationFlagFormGroup.get('isAddMode')?.value;
    if (!this.isAddMode) {
      this.donationFlagFormGroup.get('name')?.clearValidators();
      this.donationFlagFormGroup.get('description')?.clearValidators();
      this.donationFlagFormGroup.get('selectedFlag')?.setValidators(Validators.required);
    } else {
      this.donationFlagFormGroup.get('name')?.setValidators([Validators.required]);
      this.donationFlagFormGroup.get('description')?.setValidators([Validators.required]);
      this.donationFlagFormGroup.get('selectedFlag')?.clearValidators();
    }
    this.donationFlagFormGroup.get('name')?.updateValueAndValidity();
    this.donationFlagFormGroup.get('description')?.updateValueAndValidity();
    this.donationFlagFormGroup.get('selectedFlag')?.updateValueAndValidity();
  }

  searchFlags(event: any): void {
    const query = event.query;
    this.service.searchDonationFlags(query, this.campaignId).subscribe({
      next: (data) => {
        this.filteredFlags = data.map((item: any) => ({
          name: item.text, 
          id: item.value  
        }));
      },
      error: (err) => {
        console.error('Error searching donation flags:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error ? err.error : 'Failed to search donation flags. Please try again.'
        });
      }
    });
  }

  onAddFlag(): void {
    this.isEditMode = false;
    this.isAddMode = false;
    this.donationFlagFormGroup.reset();
    this.showAddEditDialog = true;
  }

  onEditFlag(flag: any): void {
    this.isEditMode = true;
    this.donationFlagFormGroup.patchValue(flag);
    this.showAddEditDialog = true;
  }

  saveFlag(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    const selectedFlag = this.donationFlagFormGroup.value.selectedFlag;
    if (this.isAddMode && this.donationFlagFormGroup.valid) {
      const model: AddDonationFlagAndAssignToDonationViewModel = {
        donationId: this.donationId,
        title: this.donationFlagFormGroup.value.name,
        description: this.donationFlagFormGroup.value.description
      };

      this.service.addDonationFlagAndAssignToDonation(model, this.campaignId).subscribe({
        next: () => {
          this.loadFlags();
          this.showAddEditDialog = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Donation flag added and assigned successfully.'
          });
        },
        error: (err) => {
          console.error('Error adding donation flag:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add and assign donation flag. Please try again.'
          });
        }
      });
    } else if (!this.isAddMode && selectedFlag) {
      this.service.addExistingDonationFlagToDonation(this.donationId, Number(selectedFlag)).subscribe({
        next: () => {
          this.loadFlags();
          this.showAddEditDialog = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Existing donation flag added successfully.'
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error ? err.error : 'Failed to add existing donation flag. Please try again.'
          });
        }
      });
    } else {
      this.donationFlagFormGroup.markAllAsTouched();
    }
  }

  closeAddEditDialog(): void {
    this.donationFlagFormGroup.reset(); 
    this.showAddEditDialog = false;
  }

  onDeleteFlag(flag: any): void {
    this.flagToDelete = flag;
    this.showDeleteDialog = true;
  }

  confirmDeleteFlag(): void {
    this.service.deleteDonationFlagToDonation(this.donationId, this.flagToDelete.donationFlagId).subscribe({
      next: () => {
        this.loadFlags();
        this.showDeleteDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Donation flag deleted successfully.'
        });
      },
      error: (err) => {
        console.error('Error deleting donation flag:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error ? err.error : 'Failed to delete donation flag. Please try again.'
        });
      }
    });
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false;
  }

  ngOnInit(): void {
    this.loadFlags();
  }
}
